import React, { useState, useMemo, useEffect, useRef } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isToday, isSameDay, addMonths, subMonths } from 'date-fns';
import { FiCalendar, FiList, FiChevronLeft, FiChevronRight, FiClock, FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { BiTask } from 'react-icons/bi';
import { apiGetRequest, apiPostRequest, apiPutRequest, apiDeleteRequest } from 'helpers/Requests';
import { toast } from 'react-toastify';
import ModalSkeleton from '../../../Modals/ModalSkeleton';
import CustomButton from '../../../common/Button/CustomButton';
import SelectOption from '../../../common/Select/Select';
import CircularLoader from '../../../common/CircularLoader/CircularLoader';
import ConfirmationModal from '../../../common/Modal/ConfirmationModal';
import useCareLabels from 'hooks/useCareLabels';

const ParticipantTasks = ({ participantInfo }) => {
  const { participantLabel } = useCareLabels();
  const [viewMode, setViewMode] = useState('list');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [taskData, setTaskData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    pt_id: '',
    title: '',
    description: '',
    due_date: '',
    due_time: '',
    priority: 'medium',
    status: 'pending',
    category: '',
    all_day: false,
    staff_notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [quickUpdating, setQuickUpdating] = useState({});
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [morePopup, setMorePopup] = useState(null); // { date: Date, tasks: [] } for calendar "more" popup
  const dropdownRefs = useRef({});

  const categoryOptions = [
    { label: 'Personal Care', value: 'personal_care' },
    { label: 'Health & Wellness', value: 'health_wellness' },
    { label: 'Social Activities', value: 'social_activities' },
    { label: 'Education', value: 'education' },
    { label: 'Employment', value: 'employment' },
    { label: 'Household Tasks', value: 'household' },
    { label: 'Other', value: 'other' },
  ];

  // Fetch tasks for this specific participant
  const fetchTasks = async () => {
    if (!participantInfo?.id) return;
    
    setLoading(true);
    try {
      const params = `?pt_id=${participantInfo.id}`;
      const res = await apiGetRequest(`/sp/participant-planner-tasks/calendar${params}`);
      if (res?.status === 200 && res?.data?.data) {
        setTaskData(res.data.data);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (participantInfo?.id) {
      fetchTasks();
    }
  }, [participantInfo?.id]);

  const tasks = taskData?.all || [];
  const grouped = {
    overdue: taskData?.overdue || [],
    today: taskData?.today || [],
    this_week: taskData?.this_week || [],
    upcoming: taskData?.upcoming || [],
    completed: taskData?.completed || [],
  };
  const statistics = taskData?.stats || {};

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  // Pad the grid to full weeks (Sun–Sat) so each date lands under the correct weekday header
  const daysInMonth = eachDayOfInterval({
    start: startOfWeek(monthStart, { weekStartsOn: 0 }),
    end: endOfWeek(monthEnd, { weekStartsOn: 0 }),
  });

  const getStatusColor = (status) => {
    if (status === 'completed') return 'bg-green-100 text-green-800 border-green-300';
    if (status === 'in_progress') return 'bg-blue-100 text-blue-800 border-blue-300';
    if (status === 'pending') return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (status === 'cancelled') return 'bg-gray-100 text-gray-800 border-gray-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getPriorityColor = (priority) => {
    if (priority === 'high') return 'text-red-600';
    if (priority === 'medium') return 'text-orange-600';
    return 'text-yellow-600';
  };

  const isOverdue = (task) => {
    if (task.status === 'completed' || task.status === 'cancelled') {
      return false;
    }
    const taskDate = new Date(task.due_date);
    const today = new Date();
    // Compare dates only (set time to start of day for both)
    taskDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return taskDate < today;
  };

  const filteredTasks = useMemo(() => {
    let filtered = tasks;
    
    // Apply priority filter first (before time-based grouping)
    // This ensures priority filtering works correctly with all tasks
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(task => {
        const taskPriority = task.priority ? String(task.priority).toLowerCase().trim() : '';
        const filterPriority = String(priorityFilter).toLowerCase().trim();
        return taskPriority === filterPriority;
      });
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(task => task.status === statusFilter);
    }
    
    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(task => task.category === categoryFilter);
    }
    
    // Apply time-based filter after other filters
    if (selectedFilter === 'overdue') {
      filtered = filtered.filter(task => {
        if (task.status === 'completed' || task.status === 'cancelled') return false;
        const taskDate = new Date(task.due_date);
        const today = new Date();
        taskDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return taskDate < today;
      });
    } else if (selectedFilter === 'today') {
      filtered = filtered.filter(task => {
        const taskDate = new Date(task.due_date);
        const today = new Date();
        taskDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return isSameDay(taskDate, today);
      });
    } else if (selectedFilter === 'this_week') {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Monday
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
      endOfWeek.setHours(23, 59, 59, 999);
      today.setHours(0, 0, 0, 0);
      
      filtered = filtered.filter(task => {
        const taskDate = new Date(task.due_date);
        taskDate.setHours(0, 0, 0, 0);
        return taskDate >= startOfWeek && taskDate <= endOfWeek && taskDate >= today;
      });
    } else if (selectedFilter === 'upcoming') {
      filtered = filtered.filter(task => {
        const taskDate = new Date(task.due_date);
        const today = new Date();
        taskDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return taskDate > today;
      });
    } else if (selectedFilter === 'completed') {
      filtered = filtered.filter(task => task.status === 'completed');
    }
    
    return filtered;
  }, [selectedFilter, statusFilter, priorityFilter, categoryFilter, tasks, grouped]);

  const getTasksForDate = (date) => {
    return filteredTasks.filter(task => {
      const taskDate = new Date(task.due_date);
      return isSameDay(taskDate, date);
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // If date is already in YYYY-MM-DD format, return as is
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }
    // Otherwise, parse and format
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTimeForInput = (timeString) => {
    if (!timeString) return '';
    // If time is already in HH:mm format, return as is
    if (typeof timeString === 'string' && timeString.match(/^\d{2}:\d{2}$/)) {
      return timeString;
    }
    // If time is in HH:mm:ss format, remove seconds
    if (typeof timeString === 'string' && timeString.match(/^\d{2}:\d{2}:\d{2}/)) {
      return timeString.substring(0, 5);
    }
    return timeString;
  };

  const formatTimeForDisplay = (timeString) => {
    if (!timeString) return '';
    // If time is already in 12-hour format with am/pm, capitalize it
    if (typeof timeString === 'string' && timeString.match(/\d{1,2}:\d{2}\s*(am|pm)/i)) {
      return timeString.replace(/(am|pm)/i, (match) => match.toUpperCase());
    }
    // If time is in 24-hour format (HH:mm), convert to 12-hour with capitalized AM/PM
    if (typeof timeString === 'string' && timeString.match(/^\d{2}:\d{2}$/)) {
      const [hours, minutes] = timeString.split(':');
      const hour24 = parseInt(hours, 10);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const period = hour24 >= 12 ? 'PM' : 'AM';
      return `${hour12}:${minutes} ${period}`;
    }
    // If time is in HH:mm:ss format, convert to 12-hour
    if (typeof timeString === 'string' && timeString.match(/^\d{2}:\d{2}:\d{2}/)) {
      const [hours, minutes] = timeString.split(':');
      const hour24 = parseInt(hours, 10);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const period = hour24 >= 12 ? 'PM' : 'AM';
      return `${hour12}:${minutes} ${period}`;
    }
    return timeString;
  };

  const handleOpenModal = (task = null) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        pt_id: participantInfo.id,
        title: task.title || '',
        description: task.description || '',
        due_date: formatDateForInput(task.due_date),
        due_time: formatTimeForInput(task.due_time),
        priority: task.priority || 'medium',
        status: task.status || 'pending',
        category: task.category || '',
        all_day: task.all_day || false,
        staff_notes: task.staff_notes || '',
      });
    } else {
      setEditingTask(null);
      setFormData({
        pt_id: participantInfo.id,
        title: '',
        description: '',
        due_date: '',
        due_time: '',
        priority: 'medium',
        status: 'pending',
        category: '',
        all_day: false,
        staff_notes: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTask(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingTask) {
        await apiPutRequest(`/sp/participant-planner-tasks/${editingTask.uuid}`, formData);
        toast.success('Task updated successfully');
      } else {
        await apiPostRequest('/sp/participant-planner-tasks', formData);
        toast.success('Task created successfully');
      }
      handleCloseModal();
      fetchTasks();
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error(editingTask ? 'Failed to update task' : 'Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (task) => {
    // Prevent opening delete modal if already deleting
    if (deleting) return;
    setTaskToDelete(task);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!taskToDelete || deleting) return;

    setDeleting(true);
    try {
      await apiDeleteRequest(`/sp/participant-planner-tasks/${taskToDelete.uuid}`);
      toast.success('Task deleted successfully');
      setShowDeleteModal(false);
      setTaskToDelete(null);
      fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(error?.response?.data?.message || 'Failed to delete task');
    } finally {
      setDeleting(false);
    }
  };

  const handleQuickUpdate = async (task, field, value) => {
    setQuickUpdating({ ...quickUpdating, [task.uuid]: true });
    setOpenDropdowns({ ...openDropdowns, [`${task.uuid}-${field}`]: false });
    
    try {
      await apiPutRequest(`/sp/participant-planner-tasks/${task.uuid}`, {
        [field]: value
      });
      toast.success(`Task ${field} updated`);
      fetchTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error(`Failed to update ${field}`);
    } finally {
      setQuickUpdating({ ...quickUpdating, [task.uuid]: false });
    }
  };

  const toggleDropdown = (taskId, field) => {
    const key = `${taskId}-${field}`;
    setOpenDropdowns({
      ...openDropdowns,
      [key]: !openDropdowns[key]
    });
  };

  const statusOptions = [
    { label: 'Pending', value: 'pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    { label: 'In Progress', value: 'in_progress', color: 'bg-blue-100 text-blue-800 border-blue-300' },
    { label: 'Completed', value: 'completed', color: 'bg-green-100 text-green-800 border-green-300' },
    { label: 'Cancelled', value: 'cancelled', color: 'bg-gray-100 text-gray-800 border-gray-300' },
  ];

  const priorityOptions = [
    { label: 'Low', value: 'low', color: 'text-yellow-600' },
    { label: 'Medium', value: 'medium', color: 'text-orange-600' },
    { label: 'High', value: 'high', color: 'text-red-600' },
  ];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(dropdownRefs.current).forEach(key => {
        const ref = dropdownRefs.current[key];
        if (ref && !ref.contains(event.target)) {
          setOpenDropdowns(prev => ({ ...prev, [key]: false }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!participantInfo?.id) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-12">
        <div className="text-center text-gray-500">{participantLabel} information not available</div>
      </div>
    );
  }

  const participantName = `${participantInfo.first_name || ''} ${participantInfo.last_name || ''}`.trim();

  return (
    <>
      <div className="bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-1 h-8 bg-gradient-to-b from-purple-500 to-indigo-500 rounded-full"></div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Tasks</h3>
                  <p className="text-sm text-gray-500 mt-1">Manage tasks for {participantName}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <CustomButton
                  clickHandler={() => handleOpenModal()}
                  label="Create Task"
                  Icon={<FiPlus />}
                  variant="primary"
                />
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    viewMode === 'list'
                      ? 'bg-purple-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <FiList className="w-4 h-4" />
                  <span className="text-sm font-medium">List</span>
                </button>
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    viewMode === 'calendar'
                      ? 'bg-purple-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <FiCalendar className="w-4 h-4" />
                  <span className="text-sm font-medium">Calendar</span>
                </button>
              </div>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
                <div className="text-2xl font-bold text-blue-700">{statistics.total || 0}</div>
                <div className="text-xs text-blue-600 mt-1">Total Tasks</div>
              </div>
              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-3 border border-red-200">
                <div className="text-2xl font-bold text-red-700">{statistics.overdue_count || 0}</div>
                <div className="text-xs text-red-600 mt-1">Overdue</div>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-3 border border-orange-200">
                <div className="text-2xl font-bold text-orange-700">{statistics.today_count || 0}</div>
                <div className="text-xs text-orange-600 mt-1">Due Today</div>
              </div>
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-3 border border-yellow-200">
                <div className="text-2xl font-bold text-yellow-700">{statistics.this_week_count || 0}</div>
                <div className="text-xs text-yellow-600 mt-1">This Week</div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200">
                <div className="text-2xl font-bold text-purple-700">{statistics.upcoming_count || 0}</div>
                <div className="text-xs text-purple-600 mt-1">Upcoming</div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 border border-green-200">
                <div className="text-2xl font-bold text-green-700">{statistics.completed_count || 0}</div>
                <div className="text-xs text-green-600 mt-1">Completed</div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex flex-wrap gap-4 items-center">
                {/* Time-based Filter */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-bold text-gray-700">Time:</label>
                  <select
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                    className="px-4 py-2 pr-8 text-sm font-medium border-2 border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 shadow-sm cursor-pointer max-w-[150px] truncate"
                    style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                    title={selectedFilter === 'all' ? 'All' : selectedFilter === 'this_week' ? 'This Week' : selectedFilter === 'in_progress' ? 'In Progress' : selectedFilter.charAt(0).toUpperCase() + selectedFilter.slice(1)}
                  >
                    <option value="all">All</option>
                    <option value="overdue">Overdue</option>
                    <option value="today">Today</option>
                    <option value="this_week">This Week</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                
                <div className="h-6 w-px bg-gray-300"></div>
                
                {/* Status, Priority, Category Filters */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-bold text-gray-700">Status:</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 pr-8 text-sm font-medium border-2 border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 shadow-sm cursor-pointer max-w-[150px] truncate"
                    style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                    title={statusFilter === 'all' ? 'All' : statusOptions.find(opt => opt.value === statusFilter)?.label || statusFilter}
                  >
                    <option value="all">All</option>
                    {statusOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm font-bold text-gray-700">Priority:</label>
                  <select
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value)}
                    className="px-4 py-2 pr-8 text-sm font-medium border-2 border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 shadow-sm cursor-pointer max-w-[150px] truncate"
                    style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                    title={priorityFilter === 'all' ? 'All' : priorityOptions.find(opt => opt.value === priorityFilter)?.label || priorityFilter}
                  >
                    <option value="all">All</option>
                    {priorityOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm font-bold text-gray-700">Category:</label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="px-4 py-2 pr-8 text-sm font-medium border-2 border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 shadow-sm cursor-pointer max-w-[180px] truncate"
                    style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                    title={categoryFilter === 'all' ? 'All' : categoryOptions.find(opt => opt.value === categoryFilter)?.label || categoryFilter}
                  >
                    <option value="all">All</option>
                    {categoryOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                
                {(selectedFilter !== 'all' || statusFilter !== 'all' || priorityFilter !== 'all' || categoryFilter !== 'all') && (
                  <>
                    <div className="h-6 w-px bg-gray-300"></div>
                    <button
                      onClick={() => {
                        setSelectedFilter('all');
                        setStatusFilter('all');
                        setPriorityFilter('all');
                        setCategoryFilter('all');
                      }}
                      className="px-4 py-2 text-sm font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg shadow-sm transition-all"
                    >
                      Clear Filters
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <CircularLoader />
            </div>
          ) : viewMode === 'calendar' ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">
                  {format(currentMonth, 'MMMM yyyy')}
                </h4>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <FiChevronLeft className="w-5 h-5 text-gray-600" />
                  </button>
                  <button
                    onClick={() => setCurrentMonth(new Date())}
                    className="px-3 py-1.5 text-sm font-medium text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    Today
                  </button>
                  <button
                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <FiChevronRight className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2">
                    {day}
                  </div>
                ))}

                {daysInMonth.map(day => {
                  const dayTasks = getTasksForDate(day);
                  const isCurrentMonth = isSameMonth(day, currentMonth);
                  const isTodayDate = isToday(day);

                  return (
                    <div
                      key={day.toString()}
                      className={`min-h-[100px] p-2 border border-gray-200 rounded-lg ${
                        !isCurrentMonth ? 'bg-gray-50' : 'bg-white'
                      } ${isTodayDate ? 'ring-2 ring-purple-500' : ''}`}
                    >
                      <div className={`text-sm font-medium mb-1 ${
                        isTodayDate ? 'text-purple-600' : isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                      }`}>
                        {format(day, 'd')}
                      </div>
                      <div className="space-y-1">
                        {dayTasks.slice(0, 2).map(task => (
                          <div
                            key={task.id || task.uuid}
                            onClick={() => handleOpenModal(task)}
                            className={`text-xs p-1 rounded cursor-pointer hover:shadow-md transition-all ${getStatusColor(task.status)}`}
                            title={task.title}
                          >
                            <div className="truncate font-medium">{task.title}</div>
                          </div>
                        ))}
                        {dayTasks.length > 2 && (
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setMorePopup({ date: day, tasks: dayTasks });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setMorePopup({ date: day, tasks: dayTasks });
                              }
                            }}
                            className="text-xs text-purple-600 font-medium cursor-pointer hover:underline"
                          >
                            +{dayTasks.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto custom-scroll">
              {filteredTasks.length === 0 ? (
                <div className="text-center py-12">
                  <BiTask className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg font-medium">No tasks found</p>
                  <p className="text-gray-400 text-sm mt-2">Create a task to get started</p>
                  <CustomButton
                    clickHandler={() => handleOpenModal()}
                    label="Create Task"
                    Icon={<FiPlus />}
                    cssClass="mt-4"
                  />
                </div>
              ) : (
                filteredTasks.map(task => {
                  const overdueTask = isOverdue(task);
                  return (
                    <div
                      key={task.id || task.uuid}
                      className={`p-4 rounded-lg border-2 transition-all hover:shadow-lg relative ${
                        overdueTask ? 'bg-red-50 border-red-300' : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            {overdueTask && (
                              <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded">
                                OVERDUE
                              </span>
                            )}

                            {/* Status Badge (Read-only) */}
                            <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(task.status)}`}>
                              {task.status.replace('_', ' ')}
                            </span>

                            {/* Priority Badge (Read-only) */}
                            <span className={`text-xs font-semibold uppercase ${getPriorityColor(task.priority)}`}>
                              {task.priority}
                            </span>

                            {task.category && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                {task.category.replace('_', ' ')}
                              </span>
                            )}
                          </div>
                          <h4 className="text-base font-semibold text-gray-900 mb-2">{task.title}</h4>
                          {task.description && (
                            <div className="mb-2">
                              <p className="text-sm font-semibold text-gray-700 mb-1">Description</p>
                              <p className="text-sm text-gray-600">{task.description}</p>
                            </div>
                          )}
                          {task.staff_notes && (
                            <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                              <span className="font-bold text-gray-700">Staff Notes: </span>
                              <span className="text-gray-600">{task.staff_notes}</span>
                            </div>
                          )}
                          {task.participant_notes && (
                            <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                              <span className="font-medium text-blue-700">{participantLabel} Notes: </span>
                              <span className="text-blue-600">{task.participant_notes}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <div className="flex items-center gap-1.5 text-sm text-gray-600">
                            <FiClock className="w-4 h-4" />
                            <span>{format(new Date(task.due_date), 'MMM d, yyyy')}</span>
                            {task.due_time && <span className="ml-1">{formatTimeForDisplay(task.due_time)}</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleOpenModal(task)}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="Edit task"
                            >
                              <FiEdit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(task)}
                              disabled={deleting}
                              className={`p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors ${deleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title={deleting ? 'Deleting...' : 'Delete task'}
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Calendar "more" tasks popup */}
      {morePopup && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20"
            onClick={() => setMorePopup(null)}
            onKeyDown={(e) => e.key === 'Escape' && setMorePopup(null)}
            aria-hidden="true"
          />
          <div
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-lg shadow-xl border border-gray-200 max-w-sm w-full max-h-[70vh] overflow-hidden flex flex-col"
            role="dialog"
            aria-label={`Tasks for ${format(morePopup.date, 'EEEE, MMM d, yyyy')}`}
          >
            <div className="px-4 py-3 border-b border-gray-200 font-medium text-gray-900">
              Tasks — {format(morePopup.date, 'EEEE, MMM d, yyyy')}
            </div>
            <div className="p-2 overflow-y-auto flex-1 space-y-1">
              {morePopup.tasks.map((task) => (
                <div
                  key={task.id || task.uuid}
                  onClick={() => {
                    handleOpenModal(task);
                    setMorePopup(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOpenModal(task);
                      setMorePopup(null);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  className={`text-sm p-2 rounded cursor-pointer hover:shadow-md transition-all ${getStatusColor(task.status)}`}
                  title={task.title}
                >
                  <div className="font-medium truncate">{task.title}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <ModalSkeleton
          isOpen={showModal}
          closeModal={handleCloseModal}
          title={editingTask ? 'Edit Task' : 'Create New Task'}
          bodyPadding="p-2"
          cssClass="max-w-3xl w-full"
        >
          <form onSubmit={handleSubmit} className="space-y-2 -mt-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter task title"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                rows="2"
                placeholder="Enter task description"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">
                  Due Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={formData.due_date}
                  onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Due Time</label>
                <>
                  <style dangerouslySetInnerHTML={{__html: `
                    .uppercase-time-input::-webkit-datetime-edit-ampm-field {
                      text-transform: uppercase !important;
                      font-variant: normal !important;
                    }
                  `}} />
                  <input
                    type="time"
                    value={formData.due_time}
                    onChange={(e) => setFormData({...formData, due_time: e.target.value})}
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent uppercase-time-input"
                  />
                </>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Category</label>
                <SelectOption
                  selectedOption={categoryOptions.find(c => c.value === formData.category) || null}
                  handleChange={(selected) => setFormData({...formData, category: selected?.value || ''})}
                  data={categoryOptions}
                  placeholder="Select category..."
                  useMenuPortal={true}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Staff Notes</label>
              <textarea
                value={formData.staff_notes}
                onChange={(e) => setFormData({...formData, staff_notes: e.target.value})}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                rows="2"
                placeholder={`Internal notes (not visible to ${participantLabel.toLowerCase()})`}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <CustomButton
                clickHandler={handleCloseModal}
                label="Cancel"
                variant="secondary"
                type="button"
              />
              <CustomButton
                label={editingTask ? 'Update Task' : 'Create Task'}
                variant="primary"
                type="submit"
                showLoading={submitting}
                isDisabled={submitting}
              />
            </div>
          </form>
        </ModalSkeleton>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        showModal={showDeleteModal}
        closeModalHandler={() => {
          if (!deleting) {
            setShowDeleteModal(false);
            setTaskToDelete(null);
          }
        }}
        submitHandler={handleDeleteConfirm}
        title="Delete Task"
        heading={taskToDelete ? `Are you sure you want to delete "${taskToDelete.title}"?` : "Are you sure you want to delete this task?"}
        isNew={true}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Delete"
        variantConfirmBtn="danger"
        confirmBtnLoader={deleting}
      />
    </>
  );
};

export default ParticipantTasks;
